import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { catchError, map, tap, shareReplay } from 'rxjs/operators';
import { ClinicService } from './clinic.service';
import { UserService } from './user.service';
import { resolveClinicIdFromLogin, storeLoginClinicContext } from '../../utils/clinic-context.util';

export interface WorkContext {
  clinicId: number | null;
  doctorId: number | null;
  clinicName: string;
  doctorName: string;
}

export interface ContextOption {
  label: string;
  value: number;
}

@Injectable({
  providedIn: 'root',
})
export class WorkContextService {
  private readonly contextSubject = new BehaviorSubject<WorkContext>(this.readFromStorage());
  readonly context$ = this.contextSubject.asObservable();

  private clinicOptions: ContextOption[] = [];
  private doctorOptions: ContextOption[] = [];
  private optionsLoaded = false;
  private loadOptions$?: Observable<void>;

  constructor(
    private clinicService: ClinicService,
    private userService: UserService,
  ) {}

  getContext(): WorkContext {
    return this.contextSubject.value;
  }

  getClinicId(): number | null {
    return this.getContext().clinicId;
  }

  getDoctorId(): number | null {
    return this.getContext().doctorId;
  }

  getClinicIdString(): string {
    const id = this.getClinicId();
    return id != null ? String(id) : '';
  }

  getDoctorIdString(): string {
    const id = this.getDoctorId();
    return id != null ? String(id) : '';
  }

  hasContext(): boolean {
    return this.getClinicId() != null && this.getDoctorId() != null;
  }

  getClinics(): ContextOption[] {
    return this.clinicOptions;
  }

  getDoctors(): ContextOption[] {
    return this.doctorOptions;
  }

  ensureOptionsLoaded(): Observable<void> {
    if (this.optionsLoaded) {
      return of(void 0);
    }

    if (this.loadOptions$) {
      return this.loadOptions$;
    }

    this.loadOptions$ = forkJoin({
      clinics: this.clinicService.getClinic().pipe(catchError(() => of({ data: [] }))),
      doctors: this.userService.getDoctors().pipe(catchError(() => of({ data: [] }))),
    }).pipe(
      tap(({ clinics, doctors }: any) => {
        this.clinicOptions = Array.isArray(clinics?.data)
          ? clinics.data.map((clinic: any) => ({ label: clinic.name, value: clinic.id }))
          : [];
        this.doctorOptions = Array.isArray(doctors?.data)
          ? doctors.data.map((doctor: any) => ({ label: doctor.name, value: doctor.id }))
          : [];
        this.optionsLoaded = true;
        this.applySmartDefaults();
      }),
      map(() => void 0),
      shareReplay(1)
    );

    return this.loadOptions$;
  }

  setContext(clinicId: number, doctorId: number): void {
    sessionStorage.setItem('clinic_id', String(clinicId));
    sessionStorage.setItem('doctor_id', String(doctorId));

    const clinicName = this.clinicOptions.find((c) => c.value === clinicId)?.label ?? '';
    const doctorName = this.doctorOptions.find((d) => d.value === doctorId)?.label ?? '';
    sessionStorage.setItem('clinic_name', clinicName);
    sessionStorage.setItem('doctor_name', doctorName);

    this.emitContext(clinicId, doctorId);
  }

  /** Temporarily switch context (e.g. calendar consultation) and remember the prior selection. */
  setContextForConsultation(clinicId: number, doctorId: number): void {
    if (sessionStorage.getItem('context_override_active') !== '1') {
      const current = this.getContext();
      if (current.clinicId != null) {
        sessionStorage.setItem('previous_clinic_id', String(current.clinicId));
      }
      if (current.doctorId != null) {
        sessionStorage.setItem('previous_doctor_id', String(current.doctorId));
      }
      if (current.clinicName) {
        sessionStorage.setItem('previous_clinic_name', current.clinicName);
      }
      if (current.doctorName) {
        sessionStorage.setItem('previous_doctor_name', current.doctorName);
      }
      sessionStorage.setItem('context_override_active', '1');
    }

    this.setContext(clinicId, doctorId);
  }

  /** Restore context saved before a consultation override, if any. */
  restorePreviousContext(): boolean {
    if (sessionStorage.getItem('context_override_active') !== '1') {
      return false;
    }

    const clinicRaw = sessionStorage.getItem('previous_clinic_id');
    const doctorRaw = sessionStorage.getItem('previous_doctor_id');

    sessionStorage.removeItem('context_override_active');
    sessionStorage.removeItem('previous_clinic_id');
    sessionStorage.removeItem('previous_doctor_id');
    sessionStorage.removeItem('previous_clinic_name');
    sessionStorage.removeItem('previous_doctor_name');

    if (!clinicRaw || !doctorRaw) {
      return false;
    }

    const clinicId = Number(clinicRaw);
    const doctorId = Number(doctorRaw);
    if (!Number.isFinite(clinicId) || !Number.isFinite(doctorId)) {
      return false;
    }

    this.setContext(clinicId, doctorId);
    return true;
  }

  hasContextOverride(): boolean {
    return sessionStorage.getItem('context_override_active') === '1';
  }

  private applySmartDefaults(): void {
    const loggedInUser = this.getLoggedInUser();
    const loginClinicId = resolveClinicIdFromLogin(loggedInUser);
    if (loginClinicId != null) {
      storeLoginClinicContext(loggedInUser);
    }

    const current = this.getContext();
    let clinicId = loginClinicId ?? current.clinicId;
    let doctorId = current.doctorId;

    if (clinicId == null && this.clinicOptions.length === 1) {
      clinicId = this.clinicOptions[0].value;
    }

    if (doctorId == null) {
      if (loggedInUser?.type === 'doctor' && loggedInUser?.id) {
        doctorId = Number(loggedInUser.id);
      } else if (this.doctorOptions.length === 1) {
        doctorId = this.doctorOptions[0].value;
      }
    }

    if (clinicId != null && doctorId != null) {
      if (clinicId !== current.clinicId || doctorId !== current.doctorId) {
        this.setContext(clinicId, doctorId);
      } else {
        this.emitContext(clinicId, doctorId);
      }
      return;
    }

    this.emitContext(clinicId, doctorId);
  }

  private emitContext(clinicId: number | null, doctorId: number | null): void {
    const clinicName =
      clinicId != null
        ? this.clinicOptions.find((c) => c.value === clinicId)?.label ?? this.readStoredName('clinic')
        : '';
    const doctorName =
      doctorId != null
        ? this.doctorOptions.find((d) => d.value === doctorId)?.label ?? this.readStoredName('doctor')
        : '';

    this.contextSubject.next({ clinicId, doctorId, clinicName, doctorName });
  }

  private readFromStorage(): WorkContext {
    const clinicRaw = sessionStorage.getItem('clinic_id');
    const doctorRaw = sessionStorage.getItem('doctor_id');
    const clinicId = clinicRaw ? Number(clinicRaw) : null;
    const doctorId = doctorRaw ? Number(doctorRaw) : null;

    return {
      clinicId: Number.isFinite(clinicId) ? clinicId : null,
      doctorId: Number.isFinite(doctorId) ? doctorId : null,
      clinicName: this.readStoredName('clinic'),
      doctorName: this.readStoredName('doctor'),
    };
  }

  private readStoredName(kind: 'clinic' | 'doctor'): string {
    return sessionStorage.getItem(`${kind}_name`) ?? '';
  }

  private getLoggedInUser(): any {
    try {
      return JSON.parse(sessionStorage.getItem('loggedInUser') || '{}');
    } catch {
      return {};
    }
  }
}
